<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import OverlayPanel from 'primevue/overlaypanel';
import { capitalizeStr, getTagsColors } from '@Helpers';

const props = defineProps<{
  field: any;
  isAside?: boolean;
  oddRow?: boolean;
  rowData: any;
  settings: any;
}>();

const extraInfoPanel = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('extraInfoPanel');
const isHovering = ref(false);
const visible = ref<boolean>(false);

const hidePublicGroup = computed(
  () => props.settings?.hidePublicGroup && props.field.includes('public')
);
const tags = computed(() => {
  const { field, settings } = props;

  if (!field) return [];
  const TAGS_IDS = field.map((tag: any) => tag?.$oid || tag);

  const VALID_TAGS = settings.specifics
    .map((field: any) => {
      if (!TAGS_IDS.includes(field?._id?.$oid)) return false;

      const { _id, color, value } = field;
      return {
        key: _id?.$oid,
        value: capitalizeStr(value),
        color: color,
      };
    })
    .filter((item: any) => !!item);

  return VALID_TAGS;
});
const backgroundColor = computed(() => {
  const { field, rowData, settings } = props;

  if (settings?.alternativeBgColor && settings?.alternativeBgColorTag === field) {
    return settings?.alternativeBgColor;
  }

  return settings?.render?.targetKey ? rowData[settings.render.targetKey] : getTagsColors(field);
});
const parsedMessage = computed(() => {
  const raw = props.rowData[props.settings?.message];
  if (!raw) return null;

  // NOTE: The expected data below comes from the microchip provider, not us, so we can't choose
  // how the data comes to be formatted correctly
  const isObject = (val: unknown): val is Record<string, unknown> =>
    val !== null && typeof val === 'object' && !Array.isArray(val);

  const tryParse = (input: string | object) => {
    if (typeof input === 'object') return input;
    if (typeof input === 'string' && input.startsWith('{') && input.endsWith('}')) {
      const parsed = JSON.parse(input);
      return isObject(parsed) ? parsed : input;
    }
    return input;
  };

  const formatKey = (key: string) =>
    key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

  const parsed = tryParse(raw);
  if (!isObject(parsed)) return parsed;

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'object' && Object.keys(value).length === 0) ||
      value === ''
    )
      continue;
    filtered[formatKey(key)] = value;
  }

  return filtered;
});

function getUniqueTags(tags: string[]) {
  let _tags = tags;

  if (props?.settings?.hidePublicGroup) _tags = tags.filter((tag: any) => tag !== 'public');

  return [...new Set(_tags)];
}

function getUniqueGroupTags(tags: any[]) {
  const seen = new Set<string>();
  const { settings } = props;

  return tags
    .map((tag: any) => {
      const tagId = tag?.$oid;
      const match = settings?.specifics?.find((g: any) => g._id === tagId);
      if (!match) return undefined;

      const text = match.label;
      if (seen.has(text)) return undefined;
      seen.add(text);

      return {
        id: match.seq_id,
        text,
        color: match.hex,
      };
    })
    .filter((item): item is { id: string; text: string; color: string } => !!item);
}

function showPanel(event: any) {
  isHovering.value = true;
  extraInfoPanel.value?.show(event);
}

function hidePanel() {
  setTimeout(() => (!isHovering.value ? extraInfoPanel.value?.hide() : ''), 100);
}
</script>

<template>
  <span
    v-if="!field || !field.length || hidePublicGroup"
    class="flex flex-col items-start gap-0.5"
  >
    -
  </span>

  <div
    v-else-if="settings.specifics && settings?.type !== 'group_litter'"
    :class="[
      isAside ? 'flex-wrap gap-1 ' : 'w-full max-w-80 gap-0.5 overflow-hidden',
      'relative flex items-start',
    ]"
    @mouseenter="showPanel"
    @mouseleave="
      isHovering = false;
      hidePanel();
    "
  >
    <Tag
      v-for="{ key, value, color } in tags"
      :class="[
        color === '#F5F7F8' ? 'text-gray-700' : 'text-white',
        isAside
          ? 'w-fit rounded-lg text-[10px] font-semibold'
          : 'tags w-fit rounded-lg py-[3px] text-[10px] font-semibold',
      ]"
      :key="key"
      :value="value"
      :style="{
        'background-color': color || '#726EB1',
      }"
    />

    <div
      v-if="tags.length > 2"
      :class="[
        'absolute right-0 h-10 w-10 bg-gradient-to-r from-transparent ',
        oddRow ? 'to-gray-100' : 'to-white',
      ]"
    ></div>

    <OverlayPanel
      ref="extraInfoPanel"
      @mouseenter="isHovering = true"
      @mouseleave="
        isHovering = false;
        hidePanel();
      "
    >
      <div class="flex max-w-64 flex-wrap gap-1">
        <Tag
          v-for="{ key, value, color } in tags"
          :class="[
            color === '#F5F7F8' ? 'text-gray-700' : 'text-white',
            'w-fit rounded-lg text-[10px] font-semibold',
          ]"
          :key="key"
          :value="value"
          :style="{
            'background-color': color || '#726EB1',
          }"
        />
      </div>
    </OverlayPanel>
  </div>

  <div
    v-else-if="Array.isArray(field) && field.length && settings?.type === 'group_litter'"
    :class="['relative flex w-full max-w-80 items-start gap-0.5 overflow-hidden']"
    @mouseenter="showPanel"
    @mouseleave="
      isHovering = false;
      hidePanel();
    "
  >
    <Tag
      v-for="{ id, text, color } in getUniqueGroupTags(field)"
      :class="[
        'tags w-fit rounded-lg py-[3px] text-[12px] font-semibold',
        backgroundColor === '#F5F7F8' ? 'text-gray-700' : 'text-white',
      ]"
      :key="id"
      :style="{ backgroundColor: color || '#94a3b8' }"
      :value="id + ' - ' + capitalizeStr(text)"
    />

    <div
      v-if="field.length >= 2"
      class="absolute right-0 h-10 w-10 bg-gradient-to-r from-transparent to-white"
    />

    <OverlayPanel
      ref="extraInfoPanel"
      @mouseenter="isHovering = true"
      @mouseleave="
        isHovering = false;
        hidePanel();
      "
    >
      <div class="flex max-w-64 flex-wrap gap-1">
        <Tag
          v-for="{ id, text, color } in getUniqueGroupTags(field)"
          :class="[
            'tags w-fit rounded-lg py-[3px] text-[12px] font-semibold',
            backgroundColor === '#F5F7F8' ? 'text-gray-700' : 'text-white',
          ]"
          :key="id"
          :style="{ backgroundColor: color || '#94a3b8' }"
          :value="id + ' - ' + capitalizeStr(text)"
        />
      </div>
    </OverlayPanel>
  </div>

  <div
    v-else-if="Array.isArray(field) && field.length"
    :class="['relative flex w-full max-w-80 items-start gap-0.5 overflow-hidden']"
    @mouseenter="showPanel"
    @mouseleave="
      isHovering = false;
      hidePanel();
    "
  >
    <Tag
      v-for="tag in getUniqueTags(field)"
      :class="[
        'tags w-16 rounded-lg bg-slate-400 py-[3px] text-[12px] font-semibold',
        backgroundColor === '#F5F7F8' ? 'text-gray-700' : 'text-white',
      ]"
      :key="tag"
      :value="capitalizeStr(tag || '')"
    />

    <div
      v-if="field.length > 2"
      class="absolute right-0 h-10 w-10 bg-gradient-to-r from-transparent to-white"
    />

    <OverlayPanel
      ref="extraInfoPanel"
      @mouseenter="isHovering = true"
      @mouseleave="
        isHovering = false;
        hidePanel();
      "
    >
      <div class="flex max-w-64 flex-wrap gap-1">
        <Tag
          v-for="tag in getUniqueTags(field)"
          :class="[
            'w-fit rounded-lg bg-slate-400 text-[12px] font-semibold',
            backgroundColor === '#F5F7F8' ? 'text-gray-700' : 'text-white',
          ]"
          :key="tag"
          :value="capitalizeStr(tag || '')"
        />
      </div>
    </OverlayPanel>
  </div>

  <div
    v-else
    class="flex items-center justify-between"
  >
    <Tag
      :class="[
        'w-fit text-[10px] font-semibold',
        backgroundColor === '#F5F7F8' ? 'text-gray-700' : 'text-white',
      ]"
      :style="{
        'background-color': backgroundColor || '#8B949E',
      }"
      :value="capitalizeStr(field)"
    />

    <Button
      v-if="rowData[settings.hasAside]"
      outlined
      class="af-button w-9 border-none"
      icon="pi pi-info-circle"
      severity="secondary"
      @click="visible = true"
    />
  </div>

  <aside>
    <Sidebar
      v-model:visible="visible"
      class="relative flex h-full w-full flex-col p-2 md:max-w-[450px]"
      position="right"
      pt:content="flex flex-col h-full"
      pt:closeButton="outline-none z-10"
      pt:header="justify-between"
    >
      <template #header>
        <div class="flex w-full justify-between pr-4">
          <div class="flex items-center gap-2">
            <i class="pi pi-exclamation-circle text-base text-gray-600"></i>

            <h2 class="text-lg font-semibold">
              {{ $t('components.forms.microchipRegister.registrationInformation') }}
            </h2>
          </div>
        </div>
      </template>

      <main class="space-y-2 text-sm">
        <div v-if="parsedMessage && typeof parsedMessage === 'object'">
          <div
            v-for="(value, key) in parsedMessage"
            class="flex gap-2"
            :key="key"
          >
            <span class="text-nowrap font-medium">{{ key }}:</span>

            <span>{{ value }}</span>
          </div>
        </div>

        <p v-else>{{ parsedMessage }}</p>
      </main>
    </Sidebar>
  </aside>
</template>

<style>
.tags > span {
  @apply overflow-hidden text-ellipsis;
}
</style>
